import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaHome, FaChartLine, FaUser, FaClock, FaComments, FaStar } from 'react-icons/fa';
import { FaArrowRightFromBracket, FaLocationDot } from 'react-icons/fa6';
import { useStudentAuth } from '../../../lib/AuthProvider';
import { getSupabase } from '../../../lib/supabaseClient';
import './StudentFeedback.css';

interface FeedbackForm {
  supervisorId: string;
  rating: number;
  comment: string;
}

interface Supervisor {
  id: string;
  first_name: string;
  last_name: string;
  office: string;
}

const StudentFeedback: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { student, loading: authLoading, signOut } = useStudentAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [feedbackForm, setFeedbackForm] = useState<FeedbackForm>({
    supervisorId: '',
    rating: 0,
    comment: ''
  });
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [supervisors, setSupervisors] = useState<Supervisor[]>([]);
  const [supervisorsLoading, setSupervisorsLoading] = useState(false);
  const [hoveredRating, setHoveredRating] = useState(0);

  // Check session validity on component mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const supabase = getSupabase();
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error || !session) {
          console.log('Session invalid in StudentFeedback, redirecting to login');
          navigate('/', { replace: true });
          return;
        }
        
        if (!student && !authLoading) {
          console.log('No student data but session exists, waiting for AuthProvider to load...');
          setTimeout(() => {
            if (!student && !authLoading) {
              console.log('Still no student data after timeout, redirecting to login');
              navigate('/', { replace: true });
            }
          }, 2000);
        }
      } catch (error) {
        console.error('Error checking session in StudentFeedback:', error);
      }
    };

    if (!authLoading) {
      checkSession();
    }
  }, [student, authLoading, navigate]);

  // Fetch supervisors when component mounts
  useEffect(() => {
    fetchSupervisors();
  }, []);

  // Fetch supervisors from the database
  const fetchSupervisors = async () => {
    setSupervisorsLoading(true);
    try {
      const supabase = getSupabase();
      const { data: supervisorsData, error } = await supabase
        .from('supervisor_profiles')
        .select('id, first_name, last_name, office')
        .eq('user_role', 'supervisor')
        .order('last_name');

      if (error) {
        console.error('Error fetching supervisors:', error);
        return;
      }

      setSupervisors(supervisorsData || []);
    } catch (error) {
      console.error('Error fetching supervisors:', error);
    } finally {
      setSupervisorsLoading(false);
    }
  };

  // Handle feedback form submission (static for now)
  const handleFeedbackSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!student) {
      alert('Student information not available. Please try again.');
      return;
    }

    // Validate required fields
    if (!feedbackForm.supervisorId || feedbackForm.rating === 0 || !feedbackForm.comment.trim()) {
      alert('Please fill in all required fields including rating.');
      return;
    }

    setFeedbackLoading(true);
    
    // Simulate submission delay (static for now)
    setTimeout(() => {
      // Reset form
      setFeedbackForm({
        supervisorId: '',
        rating: 0,
        comment: ''
      });
      setFeedbackLoading(false);
      alert('Feedback submitted successfully! (Static demo)');
    }, 1000);
  };

  // Handle form input changes
  const handleFeedbackFormChange = (field: keyof FeedbackForm, value: string | number) => {
    setFeedbackForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle rating click
  const handleRatingClick = (rating: number) => {
    setFeedbackForm(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="student-home">
      <header className="top-nav">
        <div className="logo">
          <h1>InternTr<FaLocationDot className="location-icon" />ckr</h1>
        </div>
        <button
          className="hamburger-btn"
          aria-label="Open menu"
          aria-controls="mobile-sidebar"
          aria-expanded={isMenuOpen}
          onClick={() => setIsMenuOpen(true)}
        >
          {'\u2630'}
        </button>
        <div className="nav-links">
          <button 
            onClick={() => navigate('/student/home')} 
            className={`nav-link ${isActive('/student/home') ? 'active' : ''}`}
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className={`nav-link ${isActive('/student/attendance') ? 'active' : ''}`}
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className={`nav-link ${isActive('/student/progress') ? 'active' : ''}`}
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className={`nav-link ${isActive('/student/feedback') ? 'active' : ''}`}
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className={`nav-link ${isActive('/student/profile') ? 'active' : ''}`}
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </div>
      </header>

      {/* Mobile sidebar and backdrop */}
      <div
        className={`backdrop${isMenuOpen ? ' open' : ''}`}
        onClick={() => setIsMenuOpen(false)}
        aria-hidden={!isMenuOpen}
      />
      <aside
        id="mobile-sidebar"
        className={`sidebar${isMenuOpen ? ' open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div className="sidebar-header">
          <h2>Menu</h2>
          <button
            className="close-btn"
            aria-label="Close menu"
            onClick={() => setIsMenuOpen(false)}
          >
            {'\u00D7'}
          </button>
        </div>
        <nav className="sidebar-links" onClick={() => setIsMenuOpen(false)}>
          <button 
            onClick={() => navigate('/student/home')} 
            className="nav-link"
          >
            <FaHome className="nav-icon" /> Home
          </button>
          <button 
            onClick={() => navigate('/student/attendance')} 
            className="nav-link"
          >
            <FaClock className="nav-icon" /> Attendance
          </button>
          <button 
            onClick={() => navigate('/student/progress')} 
            className="nav-link"
          >
            <FaChartLine className="nav-icon" /> Progress
          </button>
          <button 
            onClick={() => navigate('/student/feedback')} 
            className="nav-link"
          >
            <FaComments className="nav-icon" /> Feedback
          </button>
          <button 
            onClick={() => navigate('/student/profile')} 
            className="nav-link"
          >
            <FaUser className="nav-icon" /> Profile
          </button>
          <button onClick={handleSignOut} className="nav-link sign-out-btn">
            <FaArrowRightFromBracket className="nav-icon" /> Sign Out
          </button>
        </nav>
      </aside>

      <main className="main-content">
        <div className="welcome-section">
          <h1>Feedback</h1>
        </div>

        {authLoading ? (
          <div className="feedback-loading">
            <p>Loading...</p>
          </div>
        ) : student ? (
          <div className="feedback-section">
            <div className="feedback-form-container">
              <form onSubmit={handleFeedbackSubmit} className="feedback-form">
                <div className="form-group">
                  <label htmlFor="supervisor">Supervisor</label>
                  <select
                    id="supervisor"
                    value={feedbackForm.supervisorId}
                    onChange={(e) => handleFeedbackFormChange('supervisorId', e.target.value)}
                    className="form-select"
                    disabled={supervisorsLoading}
                    required
                  >
                    <option value="">
                      {supervisorsLoading 
                        ? "Loading supervisors..." 
                        : "Select Supervisor"
                      }
                    </option>
                    {supervisors.map((supervisor) => (
                      <option key={supervisor.id} value={supervisor.id}>
                        {supervisor.first_name} {supervisor.last_name} - {supervisor.office}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label>Rating</label>
                  <div className="rating-container">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        className={`star-button ${star <= (hoveredRating || feedbackForm.rating) ? 'active' : ''}`}
                        onClick={() => handleRatingClick(star)}
                        onMouseEnter={() => setHoveredRating(star)}
                        onMouseLeave={() => setHoveredRating(0)}
                        aria-label={`Rate ${star} star${star > 1 ? 's' : ''}`}
                      >
                        <FaStar />
                      </button>
                    ))}
                    <span className="rating-text">
                      {feedbackForm.rating > 0 ? `${feedbackForm.rating} / 5` : 'Select rating'}
                    </span>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="comment">Feedback</label>
                  <textarea
                    id="comment"
                    value={feedbackForm.comment}
                    onChange={(e) => handleFeedbackFormChange('comment', e.target.value)}
                    className="form-textarea"
                    placeholder="Share your experience and feedback about the supervisor..."
                    rows={5}
                    required
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    disabled={feedbackLoading}
                    className="submit-button"
                  >
                    {feedbackLoading ? 'Sending...' : 'Send Feedback'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="feedback-error">
            <p>Unable to load feedback form. Please try again later.</p>
            <button 
              className="action-button" 
              onClick={() => navigate('/student/home')}
            >
              Return to Home
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default StudentFeedback;
